import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  alpha,
  CircularProgress,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  TableBar,
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  AccessTime,
  Refresh,
  Fullscreen,
  FullscreenExit,
  Logout,
  Kitchen,
  StickyNote2,
  Cancel,
  Home,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import OrderServices from "../../services/OrderService";
import OrderItemServices from "../../services/OrderItemService";
import CompanyConfigurationServices from "../../services/CompanyConfigurationService";
import { useUserWithCompany } from "../../hooks/useCompany";
import StatusConfirmDialog from "../../components/StatusConfirmDialog";
import KDSOrderDrawer from "./KDSOrderDrawer";

const KDS = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { companyId, userType, company } = useUserWithCompany();
  const isWaiter = userType === "waiter";

  const [orders, setOrders] = useState({
    "dine-in": [],
    takeaway: [],
    delivery: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [isKitchenEnabled, setIsKitchenEnabled] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', itemName: '', onConfirm: null });
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false }));

  // selectedOrderId drives the detail drawer; selectedOrder auto-refreshes from `orders`
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const selectedOrder = useMemo(
    () => (selectedOrderId ? Object.values(orders).flat().find((o) => o.id === selectedOrderId) || null : null),
    [selectedOrderId, orders],
  );

  // Mid-tone palette — warm slate, between light and dark
  const PAGE_BG = "#e8edf5";
  const HEADER_BG = "#f0f4fa";
  const PANEL_BG = "#f0f4fa";
  const CARD_BG = "#f8fafd";
  const BORDER_LIGHT = "#cdd5e0";
  const TEXT_PRIMARY = "#1e293b";
  const TEXT_SECONDARY = "#526070";

  const orderTypes = [
    { key: "dine-in", label: "Dine-In", icon: <TableBar />, accent: "#6366f1", accentLight: "#dde3f8" },
    {
      key: "takeaway",
      label: "Takeaway",
      icon: <ShoppingBag />,
      accent: "#10b981",
      accentLight: "#d2f0e7",
    },
    {
      key: "delivery",
      label: "Delivery",
      icon: <LocalShipping />,
      accent: "#e8900a",
      accentLight: "#fae6c0",
    },
  ];

  useEffect(() => {
    if (!companyId) {
      setModulesLoading(false);
      return;
    }

    CompanyConfigurationServices.getConfigurations(companyId, "compnay_modules")
      .then((response) => {
        if (!response?.success) return;

        const items =
          response?.data?.view?.data ??
          response?.data?.view?.items ??
          response?.data?.items ??
          [];

        if (items.length > 0) {
          const value = items[0]?.value || {};
          setIsKitchenEnabled(value.Kitchen !== false);
        }
      })
      .catch((error) => {
        console.error("Error fetching company modules for KDS:", error);
      })
      .finally(() => {
        setModulesLoading(false);
      });
  }, [companyId]);

  useEffect(() => {
    if (modulesLoading) return;
    if (!isKitchenEnabled) {
      toast.info("Kitchen module is disabled in Company Configuration");
      navigate("/dashboard", { replace: true });
    }
  }, [isKitchenEnabled, modulesLoading, navigate]);

  const fetchKDSOrders = useCallback(
    async (silent = false) => {
      if (!companyId || modulesLoading || !isKitchenEnabled) return;
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);

        // Fetch active kitchen statuses — ready orders are excluded (kitchen work is done)
        const [r1, r2, r3] = await Promise.all([
          OrderServices.getAllOrders({ companyId, status: "pending", limit: 200 }),
          OrderServices.getAllOrders({ companyId, status: "confirmed", limit: 200 }),
          OrderServices.getAllOrders({ companyId, status: "preparing", limit: 200 }),
        ]);

        const allRaw = [
          ...(r1?.data?.view?.data || []),
          ...(r2?.data?.view?.data || []),
          ...(r3?.data?.view?.data || []),
        ];
        // Deduplicate by order id
        const seenIds = new Set();
        const mergedRaw = allRaw.filter((o) => !seenIds.has(o.id) && seenIds.add(o.id));

        if (mergedRaw.length > 0) {
          // Fetch full details for each order to get OrderItems
          const rawOrders = mergedRaw;
          const detailed = await Promise.all(
            rawOrders.map(async (order) => {
              try {
                const res = await OrderServices.getOrderById(order.id, {
                  companyId,
                });
                return res?.data?.view || order;
              } catch {
                return order;
              }
            }),
          );

          // Group by orderType
          const grouped = { "dine-in": [], takeaway: [], delivery: [] };
          detailed.forEach((order) => {
            const key = order.orderType || "takeaway";
            if (grouped[key]) grouped[key].push(order);
            else grouped["takeaway"].push(order);
          });

          // Sort each group by orderNumber ascending
          const sortByOrderNumber = (a, b) => {
            const numA = parseInt(a.orderNumber?.split("-").pop() || "0", 10);
            const numB = parseInt(b.orderNumber?.split("-").pop() || "0", 10);
            return numA - numB;
          };
          Object.keys(grouped).forEach((key) =>
            grouped[key].sort(sortByOrderNumber),
          );

          setOrders(grouped);
        }
      } catch (error) {
        console.error("Error fetching KDS orders:", error);
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
        setRefreshing(false);
        setCountdown(30);
      }
    },
    [companyId, isKitchenEnabled, modulesLoading],
  );

  useEffect(() => {
    if (modulesLoading || !isKitchenEnabled) return;
    fetchKDSOrders();
    const interval = setInterval(() => fetchKDSOrders(true), 30000);
    return () => clearInterval(interval);
  }, [fetchKDSOrders, isKitchenEnabled, modulesLoading]);

  // Countdown ticker
  useEffect(() => {
    const tick = setInterval(
      () => setCountdown((c) => (c > 0 ? c - 1 : 0)),
      1000,
    );
    return () => clearInterval(tick);
  }, []);

  // Fullscreen listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (modulesLoading || !isKitchenEnabled) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .catch(() => toast.error("Fullscreen unavailable"));
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    localStorage.clear();
    navigate("/login");
  };

  // ORDER_STATUS_TO_ITEM_STATUS maps an order-level advance to the
  // equivalent item status so single-item orders stay in sync.
  const ORDER_STATUS_TO_ITEM_STATUS = {
    preparing: "preparing",
    ready: "ready",
    served: "served",
    completed: "completed",
  };

  const handleStatusUpdate = async (orderId, newStatus, order = null) => {
    try {
      await OrderServices.updateOrderStatusById(orderId, newStatus);

      // When order has exactly one item, keep its status in sync automatically
      if (order?.OrderItems?.length === 1) {
        const itemStatus = ORDER_STATUS_TO_ITEM_STATUS[newStatus];
        if (itemStatus) {
          const item = order.OrderItems[0];
          await OrderItemServices.updateOrderItemStatus(item.id, {
            companyId,
            status: itemStatus,
          });
        }
      }

      toast.success(`Order marked as ${newStatus}`);
      fetchKDSOrders(true);
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleItemStatusUpdate = async (itemId, newStatus, order) => {
    try {
      // 1. Update the individual item status
      await OrderItemServices.updateOrderItemStatus(itemId, {
        companyId,
        status: newStatus,
      });

      // 2. Cascade to order status — strict one-way, only advance never go back
      const ORDER_FLOW = [
        "pending",
        "confirmed",
        "preparing",
        "ready",
        "served",
        "completed",
      ];
      const orderIdx = ORDER_FLOW.indexOf(order.status);

      if (newStatus === "preparing") {
        // Any item starting prep → advance order to preparing (if still early)
        if (orderIdx < ORDER_FLOW.indexOf("preparing")) {
          await OrderServices.updateOrderStatusById(order.id, "preparing");
        }
      } else if (newStatus === "ready") {
        // All items ready/served/completed → advance order to ready
        const allDoneOrReady = order.OrderItems?.every(
          (it) =>
            it.id === itemId ||
            ["ready", "served", "completed"].includes(it.status),
        );
        if (allDoneOrReady && orderIdx < ORDER_FLOW.indexOf("ready")) {
          await OrderServices.updateOrderStatusById(order.id, "ready");
        }
      } else if (newStatus === "served") {
        // All items served/completed → advance order to served
        const allServed = order.OrderItems?.every(
          (it) =>
            it.id === itemId || ["served", "completed"].includes(it.status),
        );
        if (allServed && orderIdx < ORDER_FLOW.indexOf("served")) {
          await OrderServices.updateOrderStatusById(order.id, "served");
        }
      }

      toast.success(`Item marked as ${newStatus}`);
      fetchKDSOrders(true);
    } catch (error) {
      console.error("Error updating item status:", error);
      toast.error("Failed to update item status");
    }
  };

  // Returns the item status config for the current user role.
  // Waiter: ready → served only (+ served → completed if order is paid).
  // Cashier/others: full kitchen flow — pending → preparing → ready.
  const getItemStatusConfig = (itemStatus, order) => {
    const isPaid = order?.paymentStatus === "paid";
    const BASE = {
      pending: {
        color: "#d97706",
        bg: "#fef3c7",
        label: "Waiting",
      },
      preparing: {
        color: "#FF6600",
        bg: "#FFF0E6",
        label: "Preparing",
      },
      ready: { color: "#059669", bg: "#d1fae5", label: "Ready" },
      served: {
        color: "#6366f1",
        bg: "#e0e7ff",
        label: "Served",
      },
      completed: {
        color: "#6b7280",
        bg: "#f3f4f6",
        label: "Completed",
      },
      cancelled: {
        color: "#dc2626",
        bg: "#fee2e2",
        label: "Cancelled",
      },
    };
    const base = BASE[itemStatus] || BASE.pending;

    if (isWaiter) {
      // Waiter can only: ready → served, and served → completed (if paid)
      if (itemStatus === "ready") {
        return {
          ...base,
          next: "served",
          nextLabel: "Mark as Served",
          nextBg: "#6366f1",
        };
      }
      if (itemStatus === "served" && isPaid) {
        return {
          ...base,
          next: "completed",
          nextLabel: "Mark as Completed",
          nextBg: "#6b7280",
        };
      }
      return { ...base, next: null, nextLabel: null, nextBg: null };
    }

    // Kitchen staff — pending → preparing → ready only
    if (itemStatus === "pending")
      return {
        ...base,
        next: "preparing",
        nextLabel: "Mark as Preparing",
        nextBg: "#FF6600",
      };
    if (itemStatus === "preparing")
      return {
        ...base,
        next: "ready",
        nextLabel: "Mark as Ready",
        nextBg: "#10b981",
      };
    return { ...base, next: null, nextLabel: null, nextBg: null };
  };

  const getUrgency = (createdAt) => {
    const mins = Math.floor((Date.now() - new Date(createdAt)) / 60000);
    if (mins >= 20)
      return { color: "#dc2626", label: "URGENT", bg: "#fee2e2", dot: "#dc2626" };
    if (mins >= 10)
      return { color: "#d97706", label: "WAITING", bg: "#fef3c7", dot: "#f59e0b" };
    return { color: "#059669", label: "FRESH", bg: "#d1fae5", dot: "#10b981" };
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const mins = Math.floor((Date.now() - new Date(dateString)) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const STATUS_CONFIG = {
    pending: {
      color: "#d97706",
      bg: "#fef3c7",
      label: "PENDING",
    },
    confirmed: {
      color: "#6366f1",
      bg: "#e0e7ff",
      label: "CONFIRMED",
    },
    preparing: {
      color: "#FF6600",
      bg: "#FFF0E6",
      label: "PREPARING",
    },
    ready: { color: "#059669", bg: "#d1fae5", label: "READY" },
    completed: {
      color: "#6b7280",
      bg: "#f3f4f6",
      label: "DONE",
    },
  };

  const renderOrderCard = (order, accentColor) => {
    const urgency = getUrgency(order.createdAt);
    const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
    const orderNum = order.orderNumber?.split("-").pop() || "N/A";

    return (
      <Box
        key={order.id}
        sx={{
          borderRadius: "18px",
          bgcolor: CARD_BG,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          border: `1px solid ${BORDER_LIGHT}`,
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 8px 32px rgba(0,0,0,0.11)",
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* ── Card Header ── tap to open detail drawer ── */}
        <Box
          onClick={() => setSelectedOrderId(order.id)}
          sx={{
            px: 2,
            pt: 2,
            pb: 1.5,
            cursor: "pointer",
            background: `linear-gradient(145deg, ${alpha(accentColor, 0.08)} 0%, ${alpha(accentColor, 0.02)} 100%)`,
            "&:hover": {
              background: `linear-gradient(145deg, ${alpha(accentColor, 0.15)} 0%, ${alpha(accentColor, 0.06)} 100%)`,
            },
          }}
        >
          {/* Top row: order number + urgency time */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

              <Typography
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: TEXT_PRIMARY,
                  lineHeight: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                #{orderNum}
              </Typography>
            </Box>

            {/* Elapsed time pill */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: "20px",
                bgcolor: urgency.bg,
              }}
            >
              <AccessTime sx={{ fontSize: 13, color: urgency.color }} />
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: urgency.color }}>
                {formatTime(order.createdAt)}
              </Typography>
            </Box>
          </Box>

          {/* Second row: table info + status */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {order.Table ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "8px",
                    bgcolor: alpha(accentColor, 0.15),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TableBar sx={{ fontSize: 14, color: accentColor }} />
                </Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: accentColor }}>
                  Table {order.Table.tableNumber}
                  {order.Table.location ? ` · ${order.Table.location}` : ""}
                </Typography>
              </Box>
            ) : (
              <Box />
            )}

            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.68rem",
                height: 22,
                bgcolor: statusCfg.bg,
                color: statusCfg.color,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
        </Box>

        {/* ── Items ── */}
        <Box sx={{ px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {order.OrderItems?.map((item, idx) => {
            const cfg = getItemStatusConfig(item.status, order);
            return (
              <Box
                key={idx}
                sx={{
                  borderRadius: "12px",
                  bgcolor: alpha(accentColor, 0.06),
                  border: `1px solid ${alpha(accentColor, 0.15)}`,
                  overflow: "hidden",
                }}
              >
                {/* Main item row */}
                <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
                  {/* Large qty block */}
                  <Box
                    sx={{
                      minWidth: 52,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(accentColor, 0.18),
                      px: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.35rem",
                        color: accentColor,
                        lineHeight: 1,
                      }}
                    >
                      {item.quantity}×
                    </Typography>
                  </Box>

                  {/* Name + variation + status */}
                  <Box sx={{ flex: 1, minWidth: 0, px: 1.25, py: 1.1 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "1rem",
                          color: TEXT_PRIMARY,
                          lineHeight: 1.3,
                        }}
                      >
                        {item.MenuItem?.name || "Item"}
                      </Typography>
                      {/* Status chip — hidden for single-item orders */}
                      {order.OrderItems?.length > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.4,
                            borderRadius: "8px",
                            bgcolor: cfg.bg,
                            flexShrink: 0,
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: cfg.color }} />
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: cfg.color, lineHeight: 1 }}>
                            {cfg.label}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {item.ItemVariation && (
                      <Typography sx={{ fontSize: "0.82rem", color: TEXT_SECONDARY, mt: 0.3, fontWeight: 500 }}>
                        {item.ItemVariation.name}
                      </Typography>
                    )}

                    {item.OrderItemModifiers?.length > 0 && (
                      <Box sx={{ mt: 0.6, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {item.OrderItemModifiers.map((mod, mIdx) => (
                          <Typography
                            key={mIdx}
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: TEXT_SECONDARY,
                              bgcolor: "#dde4ef",
                              px: 0.85,
                              py: 0.3,
                              borderRadius: "6px",
                            }}
                          >
                            + {mod.ModifierOption?.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Special instructions — full-width below */}
                {item.specialInstructions && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.85,
                      bgcolor: "#fff3e0",
                      borderTop: "1px dashed #fb923c",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.75,
                    }}
                  >
                    <StickyNote2 sx={{ fontSize: 15, color: "#ea580c", mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.85rem", color: "#b45309", fontWeight: 600, lineHeight: 1.4 }}>
                      {item.specialInstructions}
                    </Typography>
                  </Box>
                )}

                {/* Per-item action + cancel buttons — hidden when order has only one item (order CTA handles it) */}
                {order.OrderItems?.length > 1 && !['cancelled', 'completed'].includes(item.status) && (
                  <Box sx={{ px: 1.25, py: 1, borderTop: `1px solid ${alpha(accentColor, 0.15)}`, display: 'flex', gap: 1 }}>
                    {cfg.next && (
                      <Button
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemStatusUpdate(item.id, cfg.next, order);
                        }}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          py: 0.85,
                          borderRadius: "8px",
                          bgcolor: alpha(cfg.nextBg, 0.12),
                          color: cfg.nextBg,
                          textTransform: "none",
                          border: `1.5px solid ${alpha(cfg.nextBg, 0.4)}`,
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: cfg.nextBg,
                            color: "#fff",
                            border: `1.5px solid ${cfg.nextBg}`,
                            boxShadow: `0 3px 10px ${alpha(cfg.nextBg, 0.35)}`,
                          },
                          "&:active": { transform: "scale(0.98)" },
                        }}
                      >
                        {cfg.nextLabel}
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDialog({
                          open: true,
                          title: 'Cancel Item?',
                          message: 'Are you sure you want to cancel this item? This cannot be undone.',
                          itemName: item.MenuItem?.name || 'Item',
                          onConfirm: () => handleItemStatusUpdate(item.id, 'cancelled', order),
                        });
                      }}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        py: 0.85,
                        px: 1.5,
                        borderRadius: "8px",
                        bgcolor: alpha('#dc2626', 0.10),
                        color: '#dc2626',
                        textTransform: "none",
                        border: `1.5px solid ${alpha('#dc2626', 0.35)}`,
                        boxShadow: "none",
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor: '#dc2626',
                          color: "#fff",
                          border: `1.5px solid #dc2626`,
                          boxShadow: `0 3px 10px ${alpha('#dc2626', 0.35)}`,
                        },
                        "&:active": { transform: "scale(0.98)" },
                      }}
                    >
                      <Cancel sx={{ fontSize: 15 }} />
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── Order note ── */}
        {order.specialInstructions && (
          <Box sx={{ px: 2, pb: 1.25 }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.9,
                borderRadius: "10px",
                bgcolor: "#fffbeb",
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
              }}
            >
              <StickyNote2 sx={{ fontSize: 14, color: "#d97706", mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.8rem", color: "#92400e", fontWeight: 600 }}>
                {order.specialInstructions}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Order-level CTA ── */}
        {(() => {
          const ORDER_NEXT = {
            pending: { next: "preparing", label: "Start Preparing", bg: "#FF6600", hover: "#CC5200", icon: null },
            confirmed: { next: "preparing", label: "Start Preparing", bg: "#FF6600", hover: "#CC5200", icon: null },
            preparing: { next: "ready", label: "Mark as Ready", bg: "#059669", hover: "#047857", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
            ready: { next: "served", label: "Mark as Served", bg: "#6366f1", hover: "#4f46e5", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
            served: { next: "completed", label: "Complete Order", bg: "#6b7280", hover: "#4b5563", icon: null },
          };

          const step = ORDER_NEXT[order.status];
          if (!step) return null;
          if (isWaiter && order.status !== "ready") return null;
          // Multi-item orders: kitchen staff don't get or order-level CTA —
          // per-item buttons cascade the order status automatically.
          if (!isWaiter && order.OrderItems?.length > 1) return null;
          if (!isWaiter && !["pending", "confirmed", "preparing"].includes(order.status)) return null;

          return (
            <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                startIcon={step.icon}
                onClick={() => handleStatusUpdate(order.id, step.next, order)}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  py: 1.25,
                  borderRadius: "12px",
                  bgcolor: alpha(step.bg, 0.15),
                  color: step.bg,
                  textTransform: "none",
                  boxShadow: "none",
                  border: `1.5px solid ${alpha(step.bg, 0.35)}`,
                  "&:hover": {
                    bgcolor: step.bg,
                    color: "#fff",
                    border: `1.5px solid ${step.bg}`,
                    boxShadow: `0 4px 14px ${alpha(step.bg, 0.3)}`,
                  },
                  "&:active": { transform: "scale(0.99)" },
                }}
              >
                {step.label}
              </Button>
              <Tooltip title="Cancel Order">
                <Button
                  onClick={() =>
                    setConfirmDialog({
                      open: true,
                      title: 'Cancel Order?',
                      message: 'Are you sure you want to cancel this order? This cannot be undone.',
                      itemName: `Order #${order.orderNumber?.split('-').pop() || ''}`,
                      onConfirm: () => handleStatusUpdate(order.id, 'cancelled', order),
                    })
                  }
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    py: 1.25,
                    px: 2,
                    borderRadius: "12px",
                    bgcolor: alpha('#dc2626', 0.10),
                    color: '#dc2626',
                    textTransform: "none",
                    boxShadow: "none",
                    border: `1.5px solid ${alpha('#dc2626', 0.35)}`,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: '#dc2626',
                      color: "#fff",
                      border: `1.5px solid #dc2626`,
                      boxShadow: `0 4px 14px ${alpha('#dc2626', 0.3)}`,
                    },
                    "&:active": { transform: "scale(0.99)" },
                  }}
                >
                  <Cancel sx={{ fontSize: 18 }} />
                </Button>
              </Tooltip>
            </Box>
          );
        })()}

        {/* ── Cancel order button — always shown for active orders (multi-item has no CTA above) ── */}
        {!['cancelled', 'completed', 'served'].includes(order.status) && (
          (() => {
            // Only show standalone cancel when the order CTA block above is hidden
            // (multi-item orders for kitchen staff, where no CTA is rendered)
            const hiddenCTA = !isWaiter && order.OrderItems?.length > 1;
            if (!hiddenCTA) return null;
            return (
              <Box sx={{ px: 2, pb: 2 }}>
                <Button
                  fullWidth
                  startIcon={<Cancel sx={{ fontSize: 16 }} />}
                  onClick={() =>
                    setConfirmDialog({
                      open: true,
                      title: 'Cancel Order?',
                      message: 'Are you sure you want to cancel this order? This cannot be undone.',
                      itemName: `Order #${order.orderNumber?.split('-').pop() || ''}`,
                      onConfirm: () => handleStatusUpdate(order.id, 'cancelled', order),
                    })
                  }
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    py: 1.1,
                    borderRadius: "12px",
                    bgcolor: alpha('#dc2626', 0.10),
                    color: '#dc2626',
                    textTransform: "none",
                    boxShadow: "none",
                    border: `1.5px solid ${alpha('#dc2626', 0.35)}`,
                    "&:hover": {
                      bgcolor: '#dc2626',
                      color: "#fff",
                      border: `1.5px solid #dc2626`,
                      boxShadow: `0 4px 14px ${alpha('#dc2626', 0.3)}`,
                    },
                    "&:active": { transform: "scale(0.99)" },
                  }}
                >
                  Cancel Order
                </Button>
              </Box>
            );
          })()
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "20px",
              bgcolor: "#6366f1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
              boxShadow: "0 8px 32px rgba(99,102,241,0.35)",
            }}
          >
            <Kitchen sx={{ fontSize: 44, color: "#fff" }} />
          </Box>
          <CircularProgress size={36} sx={{ color: "#6366f1", mb: 2 }} />
          <Typography
            sx={{ color: TEXT_SECONDARY, fontSize: "0.95rem", fontWeight: 500 }}
          >
            Loading Kitchen Orders…
          </Typography>
        </Box>
      </Box>
    );
  }

  const totalOrders = Object.values(orders).reduce(
    (s, arr) => s + arr.length,
    0,
  );

  return (
    <Box
      sx={{
        bgcolor: PAGE_BG,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <StatusConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm || (() => { })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        itemName={confirmDialog.itemName}
        newStatus={false}
        statusLabel="Cancelled"
      />
      <KDSOrderDrawer
        open={Boolean(selectedOrder)}
        order={selectedOrder}
        onClose={() => setSelectedOrderId(null)}
        isWaiter={isWaiter}
        companyInfo={company}
        onStatusUpdate={handleStatusUpdate}
        onItemStatusUpdate={handleItemStatusUpdate}
        onRequestCancelOrder={(o) =>
          setConfirmDialog({
            open: true,
            title: 'Cancel Order?',
            message: 'Are you sure you want to cancel this order? This cannot be undone.',
            itemName: `Order #${o.orderNumber?.split('-').pop() || ''}`,
            onConfirm: () => handleStatusUpdate(o.id, 'cancelled', o),
          })
        }
        onRequestCancelItem={(item, o) =>
          setConfirmDialog({
            open: true,
            title: 'Cancel Item?',
            message: 'Are you sure you want to cancel this item? This cannot be undone.',
            itemName: item.MenuItem?.name || 'Item',
            onConfirm: () => handleItemStatusUpdate(item.id, 'cancelled', o),
          })
        }
      />
      {/* ── HEADER ── */}
      <Box
        sx={{
          px: 3,
          py: 1.25,
          bgcolor: HEADER_BG,
          borderBottom: `1px solid ${BORDER_LIGHT}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Left: brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
            }}
          >
            <Kitchen sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: TEXT_PRIMARY,
                lineHeight: 1.1,
              }}
            >
              Kitchen Display
            </Typography>
            <Typography
              sx={{ fontSize: "0.7rem", color: TEXT_SECONDARY }}
            >
              {new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          </Box>
        </Box>

        {/* Center: order counts */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Badge badgeContent={totalOrders} color="error" max={99}>
            <Chip
              label={`${totalOrders} Active Order${totalOrders !== 1 ? "s" : ""}`}
              sx={{
                fontWeight: 600,
                bgcolor: "#dde4ef",
                color: TEXT_PRIMARY,
                fontSize: "0.88rem",
                border: `1px solid ${BORDER_LIGHT}`,
              }}
            />
          </Badge>
        </Box>

        {/* Right: controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Refresh countdown */}
          <Tooltip title={`Auto-refresh in ${countdown}s`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.6,
                borderRadius: "10px",
                bgcolor: "#e8edf5",
                border: `1px solid ${BORDER_LIGHT}`,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "#dde4ef", borderColor: "#b0bccf" },
              }}
              onClick={() => fetchKDSOrders(true)}
            >
              <Refresh
                sx={{
                  fontSize: 18,
                  color: "#6366f1",
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: TEXT_SECONDARY,
                  fontWeight: 600,
                  minWidth: 22,
                }}
              >
                {countdown}s
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title="Dashboard">
            <IconButton
              onClick={() => navigate("/dashboard")}
              sx={{
                color: TEXT_SECONDARY,
                bgcolor: "#e8edf5",
                border: `1px solid ${BORDER_LIGHT}`,
                borderRadius: "10px",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "#dde4ef", color: TEXT_PRIMARY },
              }}
            >
              <Home fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <IconButton
              onClick={toggleFullscreen}
              sx={{
                color: TEXT_SECONDARY,
                bgcolor: "#e8edf5",
                border: `1px solid ${BORDER_LIGHT}`,
                borderRadius: "10px",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "#dde4ef", color: TEXT_PRIMARY },
              }}
            >
              {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Logout">
            <IconButton
              onClick={handleLogout}
              sx={{
                color: "#dc2626",
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "10px",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "#fee2e2" },
              }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── COLUMNS ── */}
      <Box
        sx={{ flex: 1, display: "flex", gap: 2, p: 2, overflow: "hidden" }}
      >
        {orderTypes.map((type) => {
          const typeOrders = orders[type.key] || [];

          return (
            <Box
              key={type.key}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                bgcolor: PANEL_BG,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                border: `1px solid ${BORDER_LIGHT}`,
              }}
            >
              {/* Column header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  background: `linear-gradient(135deg, ${type.accentLight} 0%, ${PANEL_BG} 100%)`,
                  borderBottom: `1px solid ${BORDER_LIGHT}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      bgcolor: alpha(type.accent, 0.12),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(type.icon, {
                      sx: { fontSize: 20, color: type.accent },
                    })}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1rem",
                      color: type.accent,
                    }}
                  >
                    {type.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    minWidth: 30,
                    height: 30,
                    borderRadius: "8px",
                    bgcolor: type.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 3px 8px ${alpha(type.accent, 0.35)}`,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#fff" }}
                  >
                    {typeOrders.length}
                  </Typography>
                </Box>
              </Box>

              {/* Orders scroll area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 1.5,
                  bgcolor: "#e4eaf3",
                  "&::-webkit-scrollbar": { width: "5px" },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#cbd5e1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    bgcolor: "#94a3b8",
                  },
                }}
              >
                {typeOrders.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      py: 8,
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: alpha(type.accent, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {React.cloneElement(type.icon, {
                        sx: { fontSize: 28, color: alpha(type.accent, 0.5) },
                      })}
                    </Box>
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    >
                      No {type.label} Orders
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#10b981" }} />
                      <Typography
                        sx={{
                          color: "#b0bec5",
                          fontSize: "0.75rem",
                        }}
                      >
                        Watching for new orders…
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {typeOrders.map((order) =>
                      renderOrderCard(order, type.accent),
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default KDS;
